/*
 * Non-actionable browser-error filters for the control-panel Sentry bootstrap
 * (see sentry.js). These predicates are kept in a dependency-free module so the
 * filtering rules can be unit-tested in isolation, without booting the SDK.
 */

/*
 * Marker properties of a jQuery.Event. A jQuery.Event carries a string `type`
 * plus several properties jQuery adds while dispatching or triggering, so
 * requiring at least two of these markers avoids matching ordinary plain
 * objects that merely have a `type` field.
 */
const arrEventMarkers = [
	'isTrigger',
	'delegateTarget',
	'handleObj',
	'currentTarget',
	'isDefaultPrevented',
	'stopPropagation',
	'namespace',
];

/*
 * Detect a DOM or jQuery Event object that was thrown instead of an Error.
 * Legacy jQuery form handlers and third-party tag-manager scripts sometimes
 * throw an event object from inside a native event handler; Sentry then catches
 * the non-Error and reports it as "Object captured as exception with keys: ..."
 * with no stack trace and no source location, which is impossible to action and
 * floods the project.
 */
export function isThrownEventObject ( objCandidate ) {
	// Real Errors and non-objects are always kept.
	if ( !objCandidate || typeof objCandidate !== 'object' || objCandidate instanceof Error ) {
		return false;
	}

	// A genuine native DOM Event (thrown from a browser event handler).
	if ( typeof Event !== 'undefined' && objCandidate instanceof Event ) {
		return true;
	}

	// A jQuery.Event: string `type` plus at least two dispatch/trigger markers.
	if ( typeof objCandidate.type !== 'string' ) {
		return false;
	}

	let intHits = 0;

	for ( const strMarker of arrEventMarkers ) {
		if ( strMarker in objCandidate ) {
			intHits += 1;
		}
	}

	return intHits >= 2;
}

/*
 * Detect a plain object with no enumerable own keys.
 * Safari 26 (and some extensions) reject promises with `{}`,
 * or with a CustomEvent whose properties are non-enumerable, so `Object.keys` is empty.
 * Sentry then reports "Object captured as promise rejection with keys: [object has no keys]" with no stack,
 * which cannot be actioned.
 */
export function isEmptyThrownObject ( objCandidate ) {
	if ( !objCandidate || typeof objCandidate !== 'object' || objCandidate instanceof Error ) {
		return false;
	}

	if ( Array.isArray( objCandidate ) ) {
		return false;
	}

	return Object.keys( objCandidate ).length === 0;
}

/*
 * True when at least one exception value came from the unhandled
 * onerror / onunhandledrejection global handler.
 * Explicit captureException calls have a real call site and must not be dropped.
 */
function hasUnhandledGlobalHandler ( event ) {
	const arrValues = event && event.exception && event.exception.values;

	if ( !arrValues || arrValues.length === 0 ) {
		return false;
	}

	for ( const objValue of arrValues ) {
		const objMechanism = objValue && objValue.mechanism;

		if ( objMechanism
			&& objMechanism.handled === false
			&& typeof objMechanism.type === 'string'
			&& objMechanism.type.indexOf( 'global_handlers' ) !== -1 ) {
			return true;
		}
	}

	return false;
}

/*
 * Detect the serialized form of an empty-object or Event unhandledrejection.
 * Used when hint.originalException is missing (some onerror paths),
 * or when Safari's CustomEvent fails `instanceof Event` across realms.
 */
export function isUnhandledEmptyObjectCapture ( event ) {
	const arrValues = event && event.exception && event.exception.values;

	if ( !arrValues || arrValues.length === 0 ) {
		return false;
	}

	for ( const objValue of arrValues ) {
		const objMechanism = objValue && objValue.mechanism;

		if ( !objMechanism
			|| objMechanism.handled !== false
			|| typeof objMechanism.type !== 'string'
			|| objMechanism.type.indexOf( 'global_handlers' ) === -1 ) {
			continue;
		}

		const strValue = typeof objValue.value === 'string' ? objValue.value : '';

		if ( strValue.indexOf( 'Object captured as ' ) === 0
			&& strValue.indexOf( '[object has no keys]' ) !== -1 ) {
			return true;
		}

		// Safari sometimes rejects with a CustomEvent.
		// Sentry serializes that as "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection".
		if ( ( /^Event `/ ).test( strValue )
			&& strValue.indexOf( 'captured as promise rejection' ) !== -1 ) {
			return true;
		}
	}

	return false;
}

/*
 * Combined predicate for beforeSend: drop the Safari empty-object / CustomEvent unhandledrejection,
 * whether we still have the original value or only Sentry's serialized exception string.
 */
export function isUnhandledEmptyObjectRejection ( event, hint ) {
	if ( hint && isEmptyThrownObject( hint.originalException ) && hasUnhandledGlobalHandler( event ) ) {
		return true;
	}

	return isUnhandledEmptyObjectCapture( event );
}

/*
 * Detect an uncaught error whose stack resolves only to the top-level HTML
 * document instead of a real script file. On iOS every browser runs on WebKit,
 * and browser extensions, content blockers and in-app browsers all inject
 * scripts into the page; when that injected code fails to parse or throws,
 * WebKit's global onerror reports it against the document URL with a synthetic
 * "line 1, column 0" frame that has no function and no source. Such an event
 * exposes no location that maps back to our own bundle, so it cannot be
 * actioned and only floods the project (e.g. the recurring
 * "SyntaxError: No identifiers allowed directly after numeric literal" seen
 * only on Firefox iOS).
 *
 * The match is deliberately narrow: it fires only for unhandled global-handler
 * reports whose every frame is such an opaque document frame. A genuine error
 * in our code always resolves to a .js file frame (e.g.
 * /cp/dist/scripts/login.js) with a real line and column, or is captured
 * explicitly with a call site, so neither case is ever dropped.
 */
export function isInjectedScriptError ( event ) {
	const arrValues = event && event.exception && event.exception.values;

	if ( !arrValues || arrValues.length === 0 ) {
		return false;
	}

	let blnFromGlobalHandler = false;
	let intFrames = 0;

	for ( const objValue of arrValues ) {
		const objMechanism = objValue && objValue.mechanism;

		// Only unhandled onerror / onunhandledrejection reports qualify:
		// Anything captured explicitly has a real call site worth keeping.
		if ( objMechanism
			&& objMechanism.handled === false
			&& typeof objMechanism.type === 'string'
			&& objMechanism.type.indexOf( 'global_handlers' ) !== -1 ) {
			blnFromGlobalHandler = true;
		}

		const objStack = objValue && objValue.stacktrace;
		const arrFrames = objStack && objStack.frames;

		if ( !arrFrames ) {
			continue;
		}

		for ( const objFrame of arrFrames ) {
			intFrames += 1;

			const strFile = ( objFrame && objFrame.filename ) || '';

			// A real script frame points at a .js file or names a function;
			// Either makes the error actionable, so keep the whole event.
			if ( ( /\.js(\?|$)/ ).test( strFile ) || ( objFrame && objFrame.function ) ) {
				return false;
			}
		}
	}

	return blnFromGlobalHandler && intFrames > 0;
}

/*
 * Browser phrasings of the parse error raised when a `<script>` tag receives an
 * HTML document instead of JavaScript, i.e. the parser hits the leading `<` of
 * `<!DOCTYPE html>` / `<html>`. The wording differs per engine (Chrome/Safari
 * say "Unexpected token '<'", Firefox "expected expression, got '<'"), so match
 * the `<` marker rather than a single fixed string.
 */
const arrHtmlTokenMessages = [
	'unexpected token \'<\'',
	'unexpected token <',
	'expected expression, got \'<\'',
	'expected expression, got <',
];

/*
 * Detect an unhandled SyntaxError caused by an HTML document being served where
 * a JavaScript chunk was expected. The control-panel bundle lazy-loads each
 * component through a webpack dynamic import() (see app.js / login.js); webpack
 * injects a <script> tag for the chunk, so a non-JS response (a stale chunk that
 * 200s to an error/challenge page after a deploy, an edge interstitial, or a bot
 * that mishandles the request) makes the parser choke on the leading `<`. That
 * failure surfaces through the global onerror handler, not the import() promise,
 * so the try/catch around the import cannot swallow it. The error is not
 * actionable in our code — the served HTML is the problem, not the chunk — and
 * only floods the project, so drop it here.
 *
 * The match is deliberately narrow: only unhandled global-handler SyntaxErrors
 * whose message names the `<` token qualify. Our built chunks never start with
 * `<`, so a genuine syntax bug we ship would report a different offending token
 * and is never dropped.
 */
export function isHtmlDocumentScriptError ( event ) {
	const arrValues = event && event.exception && event.exception.values;

	if ( !arrValues || arrValues.length === 0 ) {
		return false;
	}

	for ( const objValue of arrValues ) {
		const objMechanism = objValue && objValue.mechanism;

		// Only unhandled onerror reports qualify: a parse error caught anywhere
		// (or captured explicitly) has a real call site worth keeping.
		if ( !objMechanism
			|| objMechanism.handled !== false
			|| typeof objMechanism.type !== 'string'
			|| objMechanism.type.indexOf( 'global_handlers' ) === -1 ) {
			continue;
		}

		if ( objValue.type !== 'SyntaxError' ) {
			continue;
		}

		const strValue = ( typeof objValue.value === 'string' ? objValue.value : '' ).toLowerCase();

		if ( arrHtmlTokenMessages.some( ( strMessage ) => strValue.indexOf( strMessage ) !== -1 ) ) {
			return true;
		}
	}

	return false;
}
